// src/hooks/useParentData.ts
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import {
  paymentsService,
  studentsService,
} from '../services/firestoreService';
import teacherActionsService from '../services/teacherActionsService';
import { Payment, Student } from '../types/firestore';

export interface ParentStats {
  totalChildren: number;
  activeChildren: number;
  totalPayments: number;
  outstandingPayments: number;
  monthlyExpenses: number;
}

export interface ParentChildData extends Student {
  upcomingSessions: number;
  recentGrades: number[];
  attendanceRate: number;
}

export interface ParentUpcomingSession {
  id: string;
  childId: string;
  childName: string;
  subject: string;
  teacher: string;
  date: Date;
  time: string;
}

const toDate = (value: any): Date => {
  if (value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (value instanceof Date) {
    return value;
  }
  return new Date(value || Date.now());
};

const isCurrentMonth = (payment: Payment): boolean => {
  const date = toDate(payment.createdAt);
  const now = new Date();
  return (
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
};

export const useParentData = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ParentStats | null>(null);
  const [children, setChildren] = useState<ParentChildData[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<
    ParentUpcomingSession[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchParentData = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // The tenant query is the canonical source for parent-child links. It
      // also returns teacherId, avoiding unauthorized reads from each child's
      // membership subcollection.
      const linkedChildren = await studentsService.getByParentId(user.id);

      const [paymentsByChild, sessionsByChild] = await Promise.all([
        Promise.all(
          linkedChildren.map(child =>
            paymentsService.getByStudentId(child.id, [child.teacherId])
          )
        ),
        Promise.all(
          linkedChildren.map(async child => {
            const sessions = await teacherActionsService.getStudentSessions(
              child.id,
              100,
              [child.teacherId]
            );

            return sessions
              .map(session => {
                const date = toDate(
                  session.date || session.startTime || session.createdAt
                );

                return {
                  id: session.id,
                  childId: child.id,
                  childName: child.name,
                  subject: session.subject || session.title || '',
                  teacher: session.teacherName || session.className || '',
                  date,
                  time:
                    session.time ||
                    date.toLocaleTimeString('ar-SA', {
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  status: session.status,
                };
              })
              .filter(
                session =>
                  session.date.getTime() >= Date.now() &&
                  session.status !== 'cancelled' &&
                  session.status !== 'completed'
              );
          })
        ),
      ]);

      const allPayments = paymentsByChild.flat();
      const allUpcomingSessions = sessionsByChild
        .flat()
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map(({ status: _status, ...session }) => session);

      const enrichedChildren: ParentChildData[] = linkedChildren.map(child => {
        const attended = child.totalClassesAttended || 0;
        const missed = child.totalClassesMissed || 0;

        return {
          ...child,
          upcomingSessions: allUpcomingSessions.filter(
            session => session.childId === child.id
          ).length,
          recentGrades:
            typeof child.averageGrade === 'number'
              ? [child.averageGrade]
              : [],
          attendanceRate: Math.round(
            (attended / Math.max(attended + missed, 1)) * 100
          ),
        };
      });

      setChildren(enrichedChildren);
      setPayments(allPayments);
      setUpcomingSessions(allUpcomingSessions);
      setStats({
        totalChildren: linkedChildren.length,
        activeChildren: linkedChildren.filter(child => child.isActive).length,
        totalPayments: allPayments.filter(payment => payment.status === 'paid')
          .length,
        outstandingPayments: allPayments.filter(payment =>
          ['pending', 'overdue'].includes(payment.status)
        ).length,
        monthlyExpenses: allPayments
          .filter(
            payment => payment.status === 'paid' && isCurrentMonth(payment)
          )
          .reduce((sum, payment) => sum + payment.amount, 0),
      });
    } catch (err) {
      console.error('Error fetching parent data:', err);
      setError('تعذر تحميل بيانات ولي الأمر. حاول مرة أخرى.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.role === 'parent') {
      fetchParentData();
    } else {
      setLoading(false);
    }
  }, [fetchParentData, user?.role]);

  return {
    stats,
    children,
    payments,
    upcomingSessions,
    loading,
    error,
    refreshData: fetchParentData,
  };
};

export default useParentData;
